import React, { useState, useEffect } from 'react';
import RoleService from '../services/roleService';
import './AuditLogViewer.css';
import { FaTimes, FaFilter, FaDownload } from 'react-icons/fa';

const AuditLogViewer = ({ businessId, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    userId: '',
    actionType: '',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const actionTypes = [
    'USER_INVITED',
    'USER_ROLE_CHANGED',
    'PERMISSION_TOGGLED',
    'ROLE_CREATED',
    'ROLE_DELETED',
    'USER_DELETED',
    'USER_ACTIVATED',
    'USER_DEACTIVATED'
  ];

  useEffect(() => {
    loadAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const cleanFilters = {};
      Object.keys(filters).forEach(key => {
        if (filters[key]) cleanFilters[key] = filters[key];
      });

      const res = await RoleService.getAuditLogs(businessId, cleanFilters);
      setLogs(res.data || []);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      alert(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    loadAuditLogs();
  };

  const clearFilters = () => {
    setFilters({
      userId: '',
      actionType: '',
      startDate: '',
      endDate: ''
    });
    setTimeout(() => loadAuditLogs(), 100);
  };

  const exportToCSV = () => {
    if (logs.length === 0) {
      alert('No logs to export');
      return;
    }

    const headers = ['Timestamp', 'User', 'Action', 'Description', 'Affected User', 'Old Value', 'New Value', 'IP Address'];
    const rows = logs.map(log => [
      new Date(log.created_at).toLocaleString(),
      log.user_email || log.user_id,
      log.action_type,
      log.action_description,
      log.affected_user_email || '',
      log.old_value || '',
      log.new_value || '',
      log.ip_address || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getActionBadgeColor = (actionType) => {
    const colors = {
      'USER_INVITED': '#10B981',
      'USER_ROLE_CHANGED': '#F59E0B',
      'PERMISSION_TOGGLED': '#3B82F6',
      'ROLE_CREATED': '#10B981',
      'ROLE_DELETED': '#EF4444',
      'USER_DELETED': '#EF4444',
      'USER_ACTIVATED': '#10B981',
      'USER_DEACTIVATED': '#F59E0B'
    };
    return colors[actionType] || '#6B7280';
  };

  const formatValue = (value) => {
    if (!value) return '';
    
    try {
      // Try to parse as JSON
      const parsed = JSON.parse(value);
      
      // If it's an object, format it as key:value pairs
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.entries(parsed)
          .map(([key, val]) => `${key}:${val}`)
          .join(', ');
      }
      
      // If it's an array, format each object
      if (Array.isArray(parsed)) {
        return parsed.map(item => {
          if (typeof item === 'object') {
            return Object.entries(item)
              .map(([key, val]) => `${key}:${val}`)
              .join(', ');
          }
          return item;
        }).join('; ');
      }
      
      return value;
    } catch (e) {
      // If not JSON, return as is
      return value;
    }
  };

  return (
    <div className="audit-log-modal-overlay">
      <div className="audit-log-modal-container">
        <div className="audit-log-header">
          <h2 className="audit-log-title">Audit Log (Owner Access Only)</h2>
          <div className="audit-log-header-actions">
            <button className="audit-log-icon-button" onClick={exportToCSV} title="Export to CSV">
              <FaDownload />
            </button>
            <button className="audit-log-close-button" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="audit-log-content">
          {loading ? (
            <div className="audit-log-loading">Loading audit logs...</div>
          ) : logs.length === 0 ? (
            <div className="audit-log-empty">No audit logs found</div>
          ) : (
            <div className="audit-log-list">
              {logs.filter(log => 
                log.action_type !== 'PERMISSION_BULK_UPDATE' && 
                log.action_type !== 'PERMISSIONS_BULK_UPDATE'
              ).map((log) => (
                <div key={log.audit_id} className="audit-log-item">
                  <div className="audit-log-item-header">
                    <span 
                      className="audit-log-badge" 
                      style={{ backgroundColor: getActionBadgeColor(log.action_type) }}
                    >
                      {log.action_type}
                    </span>
                    <span className="audit-log-timestamp">
                      {new Date(log.created_at).toLocaleDateString('en-GB', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: '2-digit' 
                      })}, {new Date(log.created_at).toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </span>
                  </div>
                  <div className="audit-log-item-body">
                    <div className="audit-log-field">
                      <strong>User:</strong> {log.user_email || `ID: ${log.user_id}`}
                    </div>
                    <div className="audit-log-field">
                      <strong>Action:</strong> {log.action_description}
                    </div>
                    {log.affected_user_email && (
                      <div className="audit-log-field">
                        <strong>Affected User:</strong> {log.affected_user_email}
                      </div>
                    )}
                    {log.old_value && (
                      <div className="audit-log-field">
                        <strong>Old Value:</strong> {formatValue(log.old_value)}
                      </div>
                    )}
                    {log.new_value && (
                      <div className="audit-log-field">
                        <strong>New Value:</strong> {formatValue(log.new_value)}
                      </div>
                    )}
                    {log.ip_address && (
                      <div className="audit-log-field">
                        <strong>IP Address:</strong> {log.ip_address}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;
